import { useQuery } from '@tanstack/react-query'
import { getSupabaseClient } from '@/lib/supabase'
import { getSupabaseUrl } from '@/lib/runtime-env'

/**
 * Organization full details interface
 * Contains complete organization information including stats and owner
 */
export interface OrganizationFullDetails {
  organization: {
    id: string
    name: string
    logoUrl: string | null
    status: 'active' | 'suspended'
    suspendedAt: string | null
    createdAt: string
    updatedAt: string
  }
  stats: {
    usersCount: number
    facilitiesCount: number
    bankConnectionsCount: number
    accountsCount: number
  }
  owner: {
    id: string
    email: string
    fullName: string | null
  } | null
}

/**
 * Organization member interface
 * Represents a user belonging to an organization
 */
export interface OrganizationMember {
  id: string
  email: string
  fullName: string | null
  role: string
  createdAt: string
}

/**
 * Organization facility interface
 * Represents a facility belonging to an organization
 */
export interface OrganizationFacility {
  id: string
  name: string
  code: string | null
  isActive: boolean
  bankConnectionsCount: number
  createdAt: string
}

/**
 * Query keys for organization admin operations
 */
const ORG_ADMIN_KEYS = {
  fullDetails: (orgId: string) => ['organization', 'full-details', orgId] as const,
  members: (orgId: string) => ['organization', 'members', orgId] as const,
  facilities: (orgId: string) => ['organization', 'facilities', orgId] as const,
}

/**
 * Hook to fetch full organization details including stats and owner
 * Used by admin to view complete organization information
 * @param orgId - Organization ID to fetch
 */
export function useOrganizationFullDetails(orgId: string | undefined) {
  return useQuery({
    queryKey: ORG_ADMIN_KEYS.fullDetails(orgId || ''),
    queryFn: async (): Promise<OrganizationFullDetails | null> => {
      if (!orgId) return null

      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-settings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'get-full-details',
            organization_id: orgId,
          }),
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to fetch organization details')
      }

      const result = await response.json()
      return result.data as OrganizationFullDetails
    },
    enabled: !!orgId,
  })
}

/**
 * Hook to fetch organization members
 * Used by admin to view all users in an organization
 * @param orgId - Organization ID to fetch members for
 */
export function useOrganizationMembers(orgId: string | undefined) {
  return useQuery({
    queryKey: ORG_ADMIN_KEYS.members(orgId || ''),
    queryFn: async (): Promise<OrganizationMember[]> => {
      if (!orgId) return []

      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-settings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'list-members',
            organization_id: orgId,
          }),
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to fetch organization members')
      }

      const result = await response.json()
      return result.data as OrganizationMember[]
    },
    enabled: !!orgId,
  })
}

/**
 * Hook to fetch organization facilities
 * Used by admin to view all facilities in an organization
 * @param orgId - Organization ID to fetch facilities for
 */
export function useOrganizationFacilities(orgId: string | undefined) {
  return useQuery({
    queryKey: ORG_ADMIN_KEYS.facilities(orgId || ''),
    queryFn: async (): Promise<OrganizationFacility[]> => {
      if (!orgId) return []

      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-settings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'list-facilities',
            organization_id: orgId,
          }),
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to fetch organization facilities')
      }

      const result = await response.json()
      return result.data as OrganizationFacility[]
    },
    enabled: !!orgId,
  })
}
